import { FormInstance } from "antd";
import { makeAutoObservable } from "mobx";
import { ZObjectRefTable } from "src/common/attrEdit/components";
import { onError } from "src/common/onError";
import { EntFilterActionType } from "src/pages/EntityFiltersPage/EntityFiltersPage.types";
import {
  loadObjectAttrinbutesReduced,
  makeObjectAttributesMap,
} from "src/pages/ManagementPage/objectsApi";
import { ZAttribute } from "src/types/ZAttribute";

import { loadEntity } from "src/pages/EntityCardPage/apiEntityCard";
import { createColsFromAttrMeta } from "src/pages/EntityFiltersPage/EntityList/compoundEntityTableStore2";
import { ZEntityRow } from "src/pages/EntityFiltersPage/EntityList/types";
import {
  loadObjectStatesInsideRefLikeAtts,
  makeObjectStatesMap,
} from "src/pages/ManagementPage/Obj2Tab/roles/rolesApi";
import { getIdNames } from "src/references/getIdNames";
import { makeDictNameById } from "src/types/AttrType";
import { ZUid } from "src/types/zUid";
import { EntitySelectTableStore } from "../EntitySelectTable/EntitySelectTableStore";
import { applyParams } from "../tables/singleLoader";
import { TableStore } from "../tables/TableStore";

const createSettingKey = (attrId: ZUid, objectId: ZUid) =>
  `objRefTableSettings${attrId}-${objectId}`;

export class ObjectRefTableStore {
  entitySelectStore: EntitySelectTableStore | null = null;

  setEntitySelectStore(store: EntitySelectTableStore) {
    this.entitySelectStore = store;
  }

  listStore: TableStore<ZEntityRow, {}> | null = null;

  loading: boolean = false;

  setLoading(flag: boolean) {
    this.loading = flag;
  }

  async init(
    attribute: ZAttribute,
    component: ZObjectRefTable,
    form: FormInstance,
    value?: string[],
    onChange?: (value?: string[]) => void,
    entityId?: ZUid,
  ) {
    try {
      this.setLoading(true);
      const { multiple, isCreateButtonAvailable, useModal, labelAtts } =
        component;
      const { id: attrId, referenceId } = attribute;
      if (!referenceId) {
        throw new Error("wrong attribute data!");
      }
      const entitySelectStore = new EntitySelectTableStore(onChange, () =>
        this.listStore?.reload(),
      );
      this.setEntitySelectStore(entitySelectStore);
      await this.entitySelectStore?.init({
        attribute,
        multiple: multiple ?? false,
        form,
        value,
        entityId,
        actionsList: isCreateButtonAvailable
          ? [EntFilterActionType.create]
          : [],
        actionsMeta: {
          useModal,
        },
      });
      if (!entitySelectStore) return;

      this.listStore = new TableStore<ZEntityRow, {}>({
        rowKey: "id",
        fnLoad: async (params) => {
          const entityList = await Promise.all(
            entitySelectStore.values.map((id) => loadEntity(id)),
          );
          return applyParams(entityList, params, () => true);
        },
        resizableColumns: true,
        settingsKey: createSettingKey(attrId, referenceId),
      });
      const attrTypeMap = makeDictNameById(await getIdNames("attrType"));
      const statesMap = makeObjectStatesMap(
        await loadObjectStatesInsideRefLikeAtts(referenceId),
      );
      /**
       * Столбцы формируются таким же образом, как и для всех таблиц, это значит,
       * что учитываются ВСЕ настройки отображения атрибутов
       */
      const attrsMap = makeObjectAttributesMap(
        await loadObjectAttrinbutesReduced(String(referenceId), {
          translate: true,
        }),
      );
      const colAtts = labelAtts?.map((id) => attrsMap[id]).filter(Boolean);
      const cols = createColsFromAttrMeta(
        colAtts as ZAttribute[],
        attrTypeMap,
        statesMap,
        { withoutStatus: true },
      );
      this.listStore.setColumns(cols);
      this.entitySelectStore?.setValues(value || []);
    } catch (error) {
      onError(error);
    } finally {
      this.setLoading(false);
    }
  }

  constructor() {
    makeAutoObservable(this);
  }
}
